import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Phone, PhoneOff, PhoneForwarded, Search, UserCheck, Loader2 } from 'lucide-react';
import { useTwilio } from '@/contexts/TwilioContext';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useDndStatus } from '@/hooks/useDndStatus';
import { toast } from 'sonner';

interface ConsultantDialerProps {
  isOpen: boolean;
  onClose: () => void;
  parkedClientName?: string;
  parkedClientPhone?: string;
}

export function ConsultantDialer({ 
  isOpen, 
  onClose,
  parkedClientName,
  parkedClientPhone 
}: ConsultantDialerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [callingUserId, setCallingUserId] = useState<string | null>(null);
  const [activeConsultantCall, setActiveConsultantCall] = useState<string | null>(null);
  const [ringAllInProgress, setRingAllInProgress] = useState(false);
  const [hadConnection, setHadConnection] = useState(false);
  
  const { makeConsultantCall, makeRingAllCall, hangUp, isOnCall, transferParkedToConsultant } = useTwilio();
  const { users = [], loading } = useAppUsers();

  // Filtrar apenas consultores (role)
  const consultants = users.filter(user => {
    const matchesSearch = searchQuery.trim() === '' || 
      user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // TODO: Filtrar por role de consultor quando implementado
    return matchesSearch && user.active;
  });

  const handleCallConsultant = async (user: any) => {
    if (!user.twilio_phone_number) {
      toast.error('Consultor não tem telefone cadastrado');
      return;
    }

    try {
      setCallingUserId(user.id);
      await makeConsultantCall(user.twilio_phone_number, user.id);
      setActiveConsultantCall(user.id);
      toast.success(`Ligando para ${user.display_name}...`);
    } catch (error) {
      console.error('Error calling consultant:', error);
      toast.error('Erro ao ligar para consultor');
      setCallingUserId(null);
      setActiveConsultantCall(null);
    }
  };

  const handleHangUpConsultant = () => {
    hangUp();
    setCallingUserId(null);
    setActiveConsultantCall(null);
  };

  const handleTransferToCurrentCall = async () => {
    if (!activeConsultantCall) return;

    try {
      // Se é Ring All, passar null para o consultantUserId (a transferência vai para quem atendeu)
      const consultantId = activeConsultantCall === 'ring-all-connected' ? null : activeConsultantCall;
      await transferParkedToConsultant(consultantId);
      setRingAllInProgress(false);
      onClose();
    } catch (error) {
      console.error('Error transferring:', error);
      toast.error('Erro na transferência');
    }
  };

  const handleRingAllTransfer = async () => {
    try {
      setRingAllInProgress(true);
      await makeRingAllCall();
      toast.success('Tocando para todos consultores disponíveis...');
      // NÃO fecha o modal - SDR vai falar com consultor que atender primeiro
    } catch (error) {
      console.error('Error in ring all transfer:', error);
      toast.error('Erro no Ring All');
      setRingAllInProgress(false);
    }
  };

  const getUserInitials = (user: any) => {
    if (user.display_name) {
      return user.display_name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return (user.first_name?.[0] || '') + (user.last_name?.[0] || '');
  };

  // Limpar callingUserId se chamada não conectar em 30 segundos
  useEffect(() => {
    if (callingUserId && !isOnCall) {
      const timeout = setTimeout(() => {
        console.log('⏱️ Call timeout - clearing callingUserId');
        setCallingUserId(null);
      }, 30000); // 30 segundos
      
      return () => clearTimeout(timeout);
    }
  }, [callingUserId, isOnCall]);

  // Se chamada conectou, limpar callingUserId e setar activeConsultantCall
  useEffect(() => {
    if (isOnCall && callingUserId) {
      console.log('✅ Call connected - setting active consultant');
      setActiveConsultantCall(callingUserId);
      setCallingUserId(null);
    } else if (!isOnCall && activeConsultantCall) {
      console.log('📴 Call ended - clearing active consultant');
      setActiveConsultantCall(null);
    }
  }, [isOnCall, callingUserId, activeConsultantCall]);

  // Detectar quando Ring All foi atendido
  useEffect(() => {
    if (ringAllInProgress && isOnCall && !activeConsultantCall) {
      console.log('✅ Ring All answered - showing transfer button');
      setActiveConsultantCall('ring-all-connected');
      setHadConnection(true);
    }
  }, [ringAllInProgress, isOnCall, activeConsultantCall]);

  // Limpar quando chamada terminar (APÓS ter conectado)
  useEffect(() => {
    if (!isOnCall && ringAllInProgress && hadConnection) {
      setRingAllInProgress(false);
      setActiveConsultantCall(null);
      setHadConnection(false);
    }
  }, [isOnCall, ringAllInProgress, hadConnection]);

  // Reset ao fechar modal
  useEffect(() => {
    if (!isOpen) {
      setRingAllInProgress(false);
      setActiveConsultantCall(null);
      setHadConnection(false);
      setCallingUserId(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Transferir Cliente para Consultor
          </DialogTitle>
          <DialogDescription className="text-base">
            Cliente em espera: <span className="font-semibold text-foreground">{parkedClientName}</span> ({parkedClientPhone})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Botão Ring All */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm mb-1">Tocar para Todos Disponíveis</p>
                <p className="text-xs text-muted-foreground">
                  Todos os consultores disponíveis receberão a chamada simultaneamente
                </p>
              </div>
              <Button
                onClick={handleRingAllTransfer}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Phone className="w-4 h-4 mr-2" />
                Ring All
              </Button>
            </div>
          </div>
          {/* Indicador de chamada ativa com consultor */}
          {activeConsultantCall && isOnCall && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <div>
                    <p className="font-semibold text-sm">
                      {activeConsultantCall === 'ring-all-connected' 
                        ? 'Em chamada com consultor' 
                        : `Em chamada com ${consultants.find(u => u.id === activeConsultantCall)?.display_name}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Alinhe os detalhes e depois transfira o cliente
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleTransferToCurrentCall}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <PhoneForwarded className="w-4 h-4 mr-2" />
                    Transferir Cliente
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleHangUpConsultant}
                  >
                    <PhoneOff className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar consultor por nome..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Lista de consultores */}
          <ScrollArea className="h-[400px] pr-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : consultants.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum consultor encontrado
              </div>
            ) : (
              <div className="space-y-2">
                {consultants.map((user) => {
                  const isCalling = callingUserId === user.id;
                  const isActive = activeConsultantCall === user.id;

                  return (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        isActive 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-background hover:bg-accent border-border'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="text-sm font-semibold">
                            {getUserInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-sm">
                            {user.display_name || `${user.first_name} ${user.last_name}`}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {user.twilio_phone_number || 'Sem telefone'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        {isActive ? (
                          <Badge className="bg-green-600">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Ativo
                          </Badge>
                        ) : isCalling ? (
                          <Button size="sm" disabled>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Chamando...
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCallConsultant(user)}
                            disabled={!user.twilio_phone_number || callingUserId !== null}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Ligar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              💡 Ligue para o consultor, alinhe os detalhes e depois transfira o cliente
            </p>
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
